import React from 'react';
import { Globe, Star, MapPin, Briefcase, Check, UserCircle2 } from 'lucide-react';

const AVAILABILITY_META = {
  available:    { label: 'Available today',  dot: 'bg-emerald-500',  text: 'text-emerald-700' },
  limited:      { label: 'Limited',          dot: 'bg-amber-500',    text: 'text-amber-700' },
  fully_booked: { label: 'Fully booked',     dot: 'bg-rose-500',     text: 'text-rose-700' },
  away:         { label: 'Away',             dot: 'bg-neutral-400',  text: 'text-neutral-500' },
};

/**
 * Profile avatar. Renders the user's picture when available, falling back to a
 * neutral person silhouette (UserCircle2). Used by booking cards, member
 * dashboard widget, admin cards, leads list — single source of truth so the
 * empty state stays consistent across the app.
 */
export function Avatar({ src, name, size = 48, className = '' }) {
  const dim = { width: size, height: size };
  if (src) {
    const url = src.startsWith('http') ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;
    return (
      <img src={url} alt={name || 'profile'}
           className={`rounded-full object-cover border border-white/40 ${className}`}
           style={dim} />
    );
  }
  return (
    <div
      data-testid="avatar-fallback"
      className={`rounded-full bg-neutral-100 text-neutral-400 flex items-center justify-center border border-neutral-200 ${className}`}
      style={dim}
      aria-label={name ? `${name} (no profile picture)` : 'No profile picture'}>
      <UserCircle2 style={{ width: size * 0.7, height: size * 0.7 }} strokeWidth={1.5} />
    </div>
  );
}

export function AvailabilityPill({ status }) {
  const meta = AVAILABILITY_META[status] || AVAILABILITY_META.available;
  return (
    <span data-testid={`availability-${status}`}
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${meta.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

/**
 * Specialist selection card used during the booking flow.
 */
export function SpecialistCard({ specialist, selected, onSelect, testId, compact }) {
  return (
    <button
      type="button"
      data-testid={testId || `specialist-card-${specialist.id}`}
      onClick={() => onSelect && onSelect(specialist)}
      className={`text-left w-full rounded-xl border transition-all p-4 ${
        selected
          ? 'border-[#f5b300] bg-[#fff8e3] shadow-[0_18px_40px_-22px_rgba(245,179,0,0.55)]'
          : 'border-neutral-200 bg-white hover:border-[#f5b300]/60 hover:bg-[#fff8e3]/40'
      }`}
    >
      <div className="flex items-start gap-3">
        <Avatar src={specialist.profile_picture_url} name={specialist.name} size={48} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[14.5px] font-extrabold text-[#14110f] truncate">{specialist.name}</p>
            {selected && <Check className="w-4 h-4 text-[#c89200]" />}
            {specialist.is_my_specialist && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-[#14110f] text-white px-1.5 py-0.5 rounded">
                Your specialist
              </span>
            )}
          </div>
          <p className="text-[12.5px] text-neutral-500 truncate">{specialist.title || 'Tax Specialist'}</p>
          <div className="mt-1.5">
            <AvailabilityPill status={specialist.availability} />
          </div>
        </div>
      </div>
      {!compact && specialist.bio && (
        <p className="mt-3 text-[12.5px] text-neutral-600 line-clamp-2 leading-19px">{specialist.bio}</p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {(specialist.languages || []).slice(0, 4).map((lang) => (
          <span key={lang}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-semibold bg-[#faf6ec] text-[#7a5b00]">
            <Globe className="w-3 h-3" />{lang}
          </span>
        ))}
        {specialist.office_location && (
          <span className="inline-flex items-center gap-1 text-[10.5px] text-neutral-500">
            <MapPin className="w-3 h-3" />{specialist.office_location}
          </span>
        )}
      </div>
    </button>
  );
}

/**
 * "Any Available Specialist" sentinel card.
 */
export function AnySpecialistCard({ selected, onSelect, testId }) {
  return (
    <button
      type="button"
      data-testid={testId || 'specialist-card-any'}
      onClick={() => onSelect && onSelect(null)}
      className={`text-left w-full rounded-xl border transition-all p-4 ${
        selected
          ? 'border-[#14110f] bg-[#14110f] text-white shadow-[0_18px_40px_-22px_rgba(20,17,15,0.55)]'
          : 'border-dashed border-neutral-300 bg-white hover:border-[#14110f]/60'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${selected ? 'bg-[#f5b300] text-[#14110f]' : 'bg-[#fff8e3] text-[#c89200]'}`}>
          <Star className="w-5 h-5" />
        </div>
        <div>
          <p className={`text-[14.5px] font-extrabold ${selected ? 'text-white' : 'text-[#14110f]'}`}>
            Any available specialist
          </p>
          <p className={`text-[12.5px] mt-0.5 ${selected ? 'text-white/70' : 'text-neutral-500'}`}>
            We&rsquo;ll match you with the best-fit specialist for your appointment.
          </p>
        </div>
      </div>
      <ul className={`mt-3 text-[11.5px] space-y-1 ${selected ? 'text-white/80' : 'text-neutral-500'}`}>
        <li className="flex items-center gap-1.5"><Briefcase className="w-3 h-3" />Considers your preferred specialist first</li>
        <li className="flex items-center gap-1.5"><Globe className="w-3 h-3" />Matches your preferred language</li>
        <li className="flex items-center gap-1.5"><Star className="w-3 h-3" />Picks the lowest workload available</li>
      </ul>
    </button>
  );
}
